import { useState, useMemo, useEffect } from 'react';
import {
    Home,
    ShoppingCart,
    Users,
    Truck,
    Grid3X3,
    PieChart,
    Calendar,
    CalendarDays,
    LayoutDashboard,
    SlidersHorizontal,
    X,
    MapPin,
    RotateCcw,
    Check,
    Loader2,
    Clock,
    Wallet,
    PackageCheck
} from 'lucide-react';
import { subDays, startOfMonth, endOfMonth, startOfDay, endOfDay, format } from 'date-fns';
import DashboardOverview from '../../pages/DashboardOverview';
import OrderAnalytics from '../../pages/OrderAnalytics';
import CustomerAnalytics from '../../pages/CustomerAnalytics';
import DeliveryPerformance from '../../pages/DeliveryPerformance';
import CategoryPerformance from '../../pages/CategoryPerformance';
import SalesMixAnalysis from '../../pages/SalesMixAnalysis';
import MonthlyAnalysis from '../../pages/MonthlyAnalysis';
import WeeklyAnalysis from '../../pages/WeeklyAnalysis';
import WalletSubscriptions from '../../pages/WalletSubscriptions';
import DeliveryOperations from '../../pages/DeliveryOperations';
import { useFilters } from '../../context/FilterContext';
import styles from './TabbedDashboard.module.css';

const tabs = [
    { id: 'overview', label: 'Overview', icon: Home, component: DashboardOverview },
    { id: 'orders', label: 'Orders', icon: ShoppingCart, component: OrderAnalytics },
    // { id: 'customers', label: 'Customers', icon: Users, component: CustomerAnalytics },
    { id: 'delivery', label: 'Delivery', icon: Truck, component: DeliveryPerformance },
    { id: 'delivery-ops', label: 'Ops Intel', icon: PackageCheck, component: DeliveryOperations },
    { id: 'wallet', label: 'Wallet & Subs', icon: Wallet, component: WalletSubscriptions },
    { id: 'categories', label: 'Categories', icon: Grid3X3, component: CategoryPerformance },
    { id: 'sales-mix', label: 'Sales Mix', icon: PieChart, component: SalesMixAnalysis },
    // { id: 'monthly', label: 'Monthly', icon: Calendar, component: MonthlyAnalysis },
    // { id: 'weekly', label: 'Weekly', icon: CalendarDays, component: WeeklyAnalysis },
];

const DATE_PRESETS = [
    { label: 'Today', getValue: () => ({ startDate: startOfDay(new Date()), endDate: endOfDay(new Date()) }) },
    { label: 'Yesterday', getValue: () => ({ startDate: startOfDay(subDays(new Date(), 1)), endDate: endOfDay(subDays(new Date(), 1)) }) },
    { label: 'Last 7 Days', getValue: () => ({ startDate: subDays(new Date(), 7), endDate: new Date() }) },
    { label: 'Last 30 Days', getValue: () => ({ startDate: subDays(new Date(), 30), endDate: new Date() }) },
    { label: 'This Month', getValue: () => ({ startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()) }) },
];

function TabbedDashboard() {
    const [activeTab, setActiveTab] = useState('overview');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    // Update time every minute
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000); // Update every minute

        return () => clearInterval(timer);
    }, []);

    const {
        selectedCities,
        availableCities,
        setCities,
        resetFilters,
        applyFilters,
        removeFilterChip,
        isApplying,
        activeFilterCount,
        appliedFilterCount,
        hasPendingChanges,
        dateRange,
        setDateRange,
        appliedFilters,
        hasCustomDateRange,
        isLoadingOrders,
        getCityName,
        clearDateFilter,
    } = useFilters();

    const ActiveComponent = useMemo(() => {
        const tab = tabs.find(t => t.id === activeTab);
        return tab ? tab.component : DashboardOverview;
    }, [activeTab]);

    const handleFilterClose = () => {
        setIsFilterOpen(false);
    };

    const handleApply = async () => {
        await applyFilters();
        setIsFilterOpen(false);
    };

    const handleReset = () => {
        resetFilters();
    };

    const handlePresetClick = (preset) => {
        setDateRange(preset.getValue());
    };

    const handleDateChange = (field, value) => {
        setDateRange({
            ...dateRange,
            [field]: new Date(value),
        });
    };

    // Radio: single city selection; clicking the active city deselects it
    const handleCitySelect = (cityId) => {
        if (selectedCities[0] === cityId) {
            setCities([]);
        } else {
            setCities([cityId]);
        }
    };

    // Format date range for display
    const formattedDateRange = useMemo(() => {
        if (!appliedFilters.dateRange?.startDate || !appliedFilters.dateRange?.endDate) {
            return null;
        }
        const start = format(appliedFilters.dateRange.startDate, 'MMM d, yyyy');
        const end = format(appliedFilters.dateRange.endDate, 'MMM d, yyyy');
        return `${start} - ${end}`;
    }, [appliedFilters.dateRange]);

    // Format current date and time
    const formattedDateTime = useMemo(() => {
        const dayName = format(currentTime, 'EEEE');
        const dateStr = format(currentTime, 'MMMM d, yyyy');
        const timeStr = format(currentTime, 'h:mm a');
        return { dayName, dateStr, timeStr, full: `${dayName}, ${dateStr}` };
    }, [currentTime]);

    return (
        <div className={styles.tabbedDashboard}>
            {/* Compact Header */}
            <header className={styles.header}>
                <div className={styles.logo}>
                    <div className={styles.logoIcon}>
                        <LayoutDashboard size={20} />
                    </div>
                    <span className={styles.logoTitle}>ABIS Pro</span>
                </div>

                {/* Center: Date/Time Display */}
                <div className={styles.dateTimeDisplay}>
                    <span className={styles.dateText}>{formattedDateTime.full}</span>
                    <span className={styles.timeText}>
                        <Clock size={12} />
                        {formattedDateTime.timeStr}
                    </span>
                </div>

                {/* Filter Button */}
                {/* Filter Button Moved to Tabs */}
            </header>

            {/* Active Filter Chips Bar */}
            {(appliedFilterCount > 0 || formattedDateRange) && (
                <div className={styles.chipsBar}>
                    <div className={styles.chipsContainer}>
                        {/* Date Range Chip */}
                        {formattedDateRange && (
                            <div className={`${styles.chip} ${styles.dateChip}`}>
                                <Calendar size={12} />
                                <span>{formattedDateRange}</span>
                                {hasCustomDateRange && (
                                    <button
                                        className={styles.chipRemove}
                                        onClick={clearDateFilter}
                                        aria-label="Clear date range"
                                    >
                                        <X size={10} />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* City Chips */}
                        {appliedFilters.cities.map(cityId => (
                            <div key={cityId} className={styles.chip}>
                                <MapPin size={12} />
                                <span>{getCityName(cityId)}</span>
                                <button
                                    className={styles.chipRemove}
                                    onClick={() => removeFilterChip('city', cityId)}
                                    aria-label={`Remove ${getCityName(cityId)}`}
                                >
                                    <X size={10} />
                                </button>
                            </div>
                        ))}


                        <button className={styles.clearAllBtn} onClick={handleReset}>
                            Clear All
                        </button>
                    </div>
                </div>
            )}

            {/* Tab Navigation */}
            <nav className={styles.tabNav} role="tablist" aria-label="Dashboard sections">
                <div className={styles.tabContainer}>
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;

                        return (
                            <button
                                key={tab.id}
                                role="tab"
                                aria-selected={isActive}
                                aria-controls={`panel-${tab.id}`}
                                className={`${styles.tab} ${isActive ? styles.active : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <Icon className={styles.tabIcon} size={18} />
                                <span className={styles.tabLabel}>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Filter Button (Right Side) */}
                <div className={styles.tabActions}>
                    <button
                        className={styles.filterBtn}
                        onClick={() => setIsFilterOpen(true)}
                        aria-label="Open filters"
                    >
                        <SlidersHorizontal size={18} />
                        <span>Filters</span>
                        {appliedFilterCount > 0 && (
                            <span className={styles.filterBadge}>{appliedFilterCount}</span>
                        )}
                    </button>
                </div>
            </nav>

            {/* Tab Content */}
            <main
                className={styles.tabContent}
                role="tabpanel"
                id={`panel-${activeTab}`}
                aria-labelledby={activeTab}
            >
                {isLoadingOrders ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: 'var(--color-primary)' }}>
                        <Loader2 size={48} className={styles.spinner} />
                        <p style={{ marginTop: 'var(--spacing-4)', fontWeight: 500 }}>Fetching live orders...</p>
                    </div>
                ) : (
                    <ActiveComponent onNavigateToTab={setActiveTab} />
                )}
            </main>

            {/* Filter Sidebar Overlay */}
            <div
                className={`${styles.filterOverlay} ${isFilterOpen ? styles.visible : ''}`}
                onClick={handleFilterClose}
                aria-hidden="true"
            />

            {/* Filter Sidebar */}
            <aside className={`${styles.filterSidebar} ${isFilterOpen ? styles.open : ''}`}>
                <div className={styles.filterSidebarHeader}>
                    <h2 className={styles.filterSidebarTitle}>
                        <SlidersHorizontal size={20} />
                        Filters
                    </h2>
                    <button
                        className={styles.filterCloseBtn}
                        onClick={handleFilterClose}
                        aria-label="Close filters"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className={styles.filterSidebarContent}>
                    {/* Date Range Section */}
                    <div className={styles.filterSection}>
                        <h3 className={styles.filterSectionTitle}>
                            <Calendar size={16} />
                            Date Range
                        </h3>
                        <div className={styles.datePresets}>
                            {DATE_PRESETS.map((preset) => (
                                <button
                                    key={preset.label}
                                    className={styles.presetBtn}
                                    onClick={() => handlePresetClick(preset)}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                        <div className={styles.dateInputs}>
                            <div className={styles.dateInput}>
                                <label>Start</label>
                                <input
                                    type="date"
                                    value={dateRange.startDate ? format(dateRange.startDate, 'yyyy-MM-dd') : ''}
                                    onChange={(e) => handleDateChange('startDate', e.target.value)}
                                    max={format(new Date(), 'yyyy-MM-dd')}
                                />
                            </div>
                            <div className={styles.dateInput}>
                                <label>End</label>
                                <input
                                    type="date"
                                    value={dateRange.endDate ? format(dateRange.endDate, 'yyyy-MM-dd') : ''}
                                    onChange={(e) => handleDateChange('endDate', e.target.value)}
                                    max={format(new Date(), 'yyyy-MM-dd')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Cities Section */}
                    <div className={styles.filterSection}>
                        <h3 className={styles.filterSectionTitle}>
                            <MapPin size={16} />
                            City
                        </h3>
                        <div className={styles.optionsList}>
                            {availableCities.map(city => (
                                <label key={city.id} className={styles.optionItem}>
                                    <input
                                        type="radio"
                                        name="city"
                                        checked={selectedCities[0] === city.id}
                                        onChange={() => handleCitySelect(city.id)}
                                    />
                                    <span className={styles.optionLabel}>{city.name}</span>
                                    <span className={styles.optionMeta}>{city.state}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar Actions */}
                <div className={styles.filterSidebarActions}>
                    <button
                        className={styles.resetBtn}
                        onClick={handleReset}
                        disabled={activeFilterCount === 0 && !hasPendingChanges}
                    >
                        <RotateCcw size={14} />
                        Reset
                    </button>
                    <button
                        className={`${styles.applyBtn} ${isApplying ? styles.loading : ''}`}
                        onClick={handleApply}
                        disabled={!hasPendingChanges && activeFilterCount === 0}
                    >
                        {isApplying ? (
                            <>
                                <Loader2 size={14} className={styles.spinner} />
                                Applying...
                            </>
                        ) : (
                            <>
                                <Check size={14} />
                                Apply Filters
                            </>
                        )}
                    </button>
                </div>
            </aside>
        </div>
    );
}

export default TabbedDashboard;
